import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Row, Col, Card, Badge, Alert, Button, Modal, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { RootState } from '../store';
import { getCustomers, useGetCustomers, useGetUsersId, useGetIntegrationsQuickbooks, postCustomers } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { Customer, CustomerSource, CustomersSummary } from '../api/generated/model';
import CustomerFilterBar, { CustomerFiltersState } from '../components/CustomerFilterBar';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';

// Import DataTable system
import { DataTable } from '../components/common/DataTable';
import type { ColumnConfig } from '../components/common/DataTable/types';


type CustomerColumnKey =
  | 'name' | 'contact_name' | 'contact_email' | 'contact_phone' | 'address'
  | 'all_sources' | 'sources_count' | 'conflicts_count'
  | 'created_at' | 'updated_at';

const CustomersPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const queryClient = useQueryClient();

  // Ref for scroll container (used by DataTable for infinite scroll)
  const cardBodyRef = useRef<HTMLDivElement>(null);

  // Filter bar toggle state
  const [showFilterBar, setShowFilterBar] = useState(false);

  // Create customer modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerContact, setNewCustomerContact] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [newCustomerAddress, setNewCustomerAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
  });
  const [validatedAddresses, setValidatedAddresses] = useState<any>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [debouncedCustomerName, setDebouncedCustomerName] = useState('');

  // Debounce customer name for duplicate checking
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCustomerName(newCustomerName.trim());
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [newCustomerName]);

  // Check if customer name already exists
  const { data: duplicateCheck } = useGetCustomers(
    { name: debouncedCustomerName, limit: 2, sort_field: 'name' as any, sort_direction: 'asc' as any },
    {
      query: {
        enabled: debouncedCustomerName.length > 0 && showCreateModal,
        staleTime: 0, // Always fetch fresh data
      },
    }
  );

  // Check for exact match (case-insensitive) since the API does partial matching
  const customerNameExists = duplicateCheck?.data && duplicateCheck.data.some(
    (customer) => customer.name.toLowerCase() === debouncedCustomerName.toLowerCase()
  );

  // Sort state
  const [sortField, setSortField] = useState<CustomerColumnKey>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Filters state
  const [filters, setFilters] = useState<CustomerFiltersState>({
    name: undefined,
    source_type: undefined,
    has_conflicts: undefined,
    missing_sling_locations: undefined,
  });

  // Fixed large limit for infinite scroll
  const FETCH_LIMIT = 50;

  // Create customer mutation
  const createCustomerMutation = useMutation({
    mutationFn: (data: {
      name: string;
      contact?: { name?: string; email?: string; phone?: string };
      address?: { line1?: string; line2?: string; city?: string; state?: string; zip?: string };
      address_confirmed?: boolean;
    }) => postCustomers(data),
    onSuccess: (response: any) => {
      // Customer created successfully (201)
      // Invalidate customers query to refetch
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      // Close modal and reset form
      setShowCreateModal(false);
      setNewCustomerName('');
      setNewCustomerContact({ name: '', email: '', phone: '' });
      setNewCustomerAddress({ line1: '', line2: '', city: '', state: '', zip: '' });
      setValidatedAddresses(null);
      setAddressConfirmed(false);
    },
    onError: (error: any) => {
      // Check if this is an address validation error (400 with validation_response)
      if (error?.response?.status === 400 && error?.response?.data?.validation_response) {
        // Address validation returned - show user the validated address
        setValidatedAddresses(error.response.data.validation_response);
        setValidationError(null);
      } else if (error?.response?.status === 400 && error?.response?.data?.message) {
        // Other 400 error (like USPS quota exceeded) - show error in validation modal
        setValidationError(error.response.data.message);
        setValidatedAddresses({}); // Empty object to trigger validation modal
      }
      // Otherwise, let the error display normally in the UI
    },
  });

  // Fetch user preferences
  const { data: userData } = useGetUsersId(user?.id || '', {
    query: {
      enabled: !!user?.id,
    }
  });

  // Fetch QuickBooks integrations to get invoice suffixes
  const { data: qbIntegrations, error: qbError, isLoading: qbLoading } = useGetIntegrationsQuickbooks();

  // Use STATE for the map so updates properly trigger re-renders
  const [integrationSuffixMap, setIntegrationSuffixMap] = useState<Record<string, string>>({});

  // Update the map when integrations load
  useEffect(() => {
    const map: Record<string, string> = {};
    if (qbIntegrations?.integrations) {
      qbIntegrations.integrations.forEach(integration => {
        if (integration.realm_id && integration.invoice_suffix) {
          map[integration.realm_id] = integration.invoice_suffix;
        }
      });
    }
    setIntegrationSuffixMap(map);
  }, [qbIntegrations, qbError, qbLoading]);

  // Row identity. Shared with the hook's duplicate-detection and DataTable.
  const getRowKey = useCallback((customer: Customer) => customer.id || '', []);

  // Build query params - for cursor-based pagination, only send cursor + limit
  const buildQueryParams = useCallback(
    (cursor: string | undefined) =>
      cursor
        ? { cursor, limit: FETCH_LIMIT }
        : {
            name: filters.name,
            source_type: filters.source_type as any,
            has_conflicts: filters.has_conflicts as any,
            missing_sling_locations: filters.missing_sling_locations as any,
            sort_field: sortField as any,
            sort_direction: sortDirection,
            limit: FETCH_LIMIT,
          },
    [filters, sortField, sortDirection]
  );

  const {
    allItems: customers,
    totalCount,
    apiSummary: summary,
    isLoading,
    error,
    hasMore,
    handleLoadMore,
  } = usePaginatedQuery<Customer, Awaited<ReturnType<typeof getCustomers>>, CustomersSummary>({
    queryKey: 'customers',
    buildQueryParams,
    queryFn: (params) => getCustomers(params as any),
    getRowKey,
  });

  // Helper function to format date as MM/DD/YYYY HH:MM
  const formatDateTime = (dateTime: string | undefined): string => {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${month}/${day}/${year} ${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };


  // Handle filter changes
  const handleFiltersChange = (newFilters: CustomerFiltersState) => {
    setFilters(newFilters);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setFilters({
      name: undefined,
      source_type: undefined,
      has_conflicts: undefined,
      missing_sling_locations: undefined,
    });
  };

  // Count active filters
  const activeFilterCount = Object.entries(filters).filter(([, value]) => value !== undefined).length;

  // Handle create customer
  const handleCreateCustomer = (e: React.FormEvent) => {
    e.preventDefault();
    if (newCustomerName.trim()) {
      const data: any = { name: newCustomerName.trim() };

      // Only include contact if at least one field is filled
      if (newCustomerContact.name || newCustomerContact.email || newCustomerContact.phone) {
        data.contact = {
          name: newCustomerContact.name || undefined,
          email: newCustomerContact.email || undefined,
          phone: newCustomerContact.phone || undefined,
        };
      }

      // Only include address if at least one field is filled
      if (newCustomerAddress.line1 || newCustomerAddress.line2 || newCustomerAddress.city ||
          newCustomerAddress.state || newCustomerAddress.zip) {
        data.address = {
          line1: newCustomerAddress.line1 || undefined,
          line2: newCustomerAddress.line2 || undefined,
          city: newCustomerAddress.city || undefined,
          state: newCustomerAddress.state || undefined,
          zip: newCustomerAddress.zip || undefined,
        };
        // Include address_confirmed flag
        data.address_confirmed = addressConfirmed;
      }

      createCustomerMutation.mutate(data);
    }
  };

  // Handle accepting the validated address
  const handleAcceptValidatedAddress = () => {
    if (validatedAddresses?.address) {
      const addr = validatedAddresses.address;
      // Update form with validated address
      setNewCustomerAddress({
        line1: addr.streetAddress || addr.streetAddressAbbreviation || '',
        line2: addr.secondaryAddress || '',
        city: addr.city || addr.cityAbbreviation || '',
        state: addr.state || '',
        zip: addr.zipCode ? `${addr.zipCode}${addr.zipPlus4 ? `-${addr.zipPlus4}` : ''}` : '',
      });
      setAddressConfirmed(true);
      setValidatedAddresses(null);
      // Re-submit with confirmed address
      setTimeout(() => {
        const data: any = { name: newCustomerName.trim() };
        if (newCustomerContact.name || newCustomerContact.email || newCustomerContact.phone) {
          data.contact = {
            name: newCustomerContact.name || undefined,
            email: newCustomerContact.email || undefined,
            phone: newCustomerContact.phone || undefined,
          };
        }
        data.address = {
          line1: addr.streetAddress || addr.streetAddressAbbreviation || undefined,
          line2: addr.secondaryAddress || undefined,
          city: addr.city || addr.cityAbbreviation || undefined,
          state: addr.state || undefined,
          zip: addr.zipCode ? `${addr.zipCode}${addr.zipPlus4 ? `-${addr.zipPlus4}` : ''}` : undefined,
        };
        data.address_confirmed = true;
        createCustomerMutation.mutate(data);
      }, 0);
    }
  };

  // Handle going back to edit the address
  const handleBackToForm = () => {
    setValidatedAddresses(null);
    setValidationError(null);
  };

  // Handle keeping original address
  const handleKeepOriginalAddress = () => {
    setAddressConfirmed(true);
    setValidatedAddresses(null);
    // Re-submit with confirmed flag
    setTimeout(() => {
      const data: any = { name: newCustomerName.trim() };
      if (newCustomerContact.name || newCustomerContact.email || newCustomerContact.phone) {
        data.contact = {
          name: newCustomerContact.name || undefined,
          email: newCustomerContact.email || undefined,
          phone: newCustomerContact.phone || undefined,
        };
      }
      if (newCustomerAddress.line1 || newCustomerAddress.line2 || newCustomerAddress.city ||
          newCustomerAddress.state || newCustomerAddress.zip) {
        data.address = {
          line1: newCustomerAddress.line1 || undefined,
          line2: newCustomerAddress.line2 || undefined,
          city: newCustomerAddress.city || undefined,
          state: newCustomerAddress.state || undefined,
          zip: newCustomerAddress.zip || undefined,
        };
        data.address_confirmed = true;
      }
      createCustomerMutation.mutate(data);
    }, 0);
  };

  // Handle row click
  const handleRowClick = (customer: Customer) => {
    navigate(`/customers/${customer.id}`);
  };

  // Handle sorting
  const handleSortChange = (field: string, direction: 'asc' | 'desc') => {
    setSortField(field as CustomerColumnKey);
    setSortDirection(direction);
  };

  // Define columns - wrap in useMemo so it recreates when integrations load
  const columns: ColumnConfig<Customer>[] = useMemo(() => {
    // Define helper functions INSIDE useMemo so they capture current integrationSuffixMap
    const getSourceDisplayName = (source: CustomerSource): string => {
      const sourceType = source.source_type;
      if (sourceType === 'quickbooks' && source.source_integration_id) {
        const suffix = integrationSuffixMap[source.source_integration_id];
        return suffix ? `QuickBooks ${suffix}` : 'QuickBooks';
      }
      return sourceType.charAt(0).toUpperCase() + sourceType.slice(1);
    };

    const renderSourceTypeBadge = (source: CustomerSource, isPrimary: boolean = false) => {
      const sourceType = source.source_type;
      const displayName = getSourceDisplayName(source);

      // Primary source always gets green (Minecraft colors)
      // Non-primary sources get blue (Roblox colors)
      let color: { bg: string; text: string };
      if (isPrimary) {
        color = { bg: '#d4edbc', text: '#11734b' }; // Minecraft green
      } else {
        color = { bg: '#bfe1f6', text: '#0a53a8' }; // Roblox blue
      }

      return (
        <span
          className="badge"
          style={{
            backgroundColor: color.bg,
            color: color.text,
          }}
        >
          {displayName}
        </span>
      );
    };

    // Helper function to format address as a single line
    const formatAddress = (customer: Customer): string => {
      const source = customer.primary_source;
      if (!source?.address) return '';

      const parts = [
        source.address.line1,
        source.address.line2,
        source.address.city,
        [source.address.state, source.address.zip].filter(Boolean).join(' '),
      ].filter(Boolean);

      return parts.join(', ');
    };

    return [
    {
      key: 'name',
      label: 'Customer Name',
      sortable: true,
      width: 250,
      renderCell: (customer) => (
        <div className="fw-semibold">{customer.name}</div>
      ),
      tooltip: (customer) => customer.name,
    },
    {
      key: 'contact_name',
      label: 'Contact Name',
      sortable: false,
      width: 180,
      renderCell: (customer) => {
        const contact = customer.primary_source?.contact;
        return contact?.name ? <div>{contact.name}</div> : null;
      },
      tooltip: (customer) => customer.primary_source?.contact?.name || '',
    },
    {
      key: 'contact_email',
      label: 'Email',
      sortable: false,
      width: 200,
      renderCell: (customer) => {
        const email = customer.primary_source?.contact?.email;
        return email ? (
          <a href={`mailto:${email}`} className="text-decoration-none" onClick={(e) => e.stopPropagation()}>
            {email}
          </a>
        ) : null;
      },
      tooltip: (customer) => customer.primary_source?.contact?.email || '',
    },
    {
      key: 'contact_phone',
      label: 'Phone',
      sortable: false,
      width: 150,
      renderCell: (customer) => {
        const phone = customer.primary_source?.contact?.phone;
        return phone ? <div>{phone}</div> : null;
      },
      tooltip: (customer) => customer.primary_source?.contact?.phone || '',
    },
    {
      key: 'address',
      label: 'Address',
      sortable: false,
      width: 300,
      renderCell: (customer) => {
        const addr = formatAddress(customer);
        return addr ? <div className="small">{addr}</div> : null;
      },
      tooltip: (customer) => formatAddress(customer),
    },
    {
      key: 'all_sources',
      label: 'Sources',
      sortable: true,
      width: 300,
      renderCell: (customer) => {
        if (!customer.all_sources || customer.all_sources.length === 0) return null;

        // Find primary source and other sources
        const primarySource = customer.all_sources.find(s => s.id === customer.primary_source_id);
        const otherSources = customer.all_sources.filter(s => s.id !== customer.primary_source_id);

        return (
          <div className="d-flex gap-1 flex-wrap">
            {primarySource && (
              <span key={primarySource.id}>
                {renderSourceTypeBadge(primarySource, true)}
              </span>
            )}
            {otherSources.map((source) => (
              <span key={source.id}>
                {renderSourceTypeBadge(source, false)}
              </span>
            ))}
          </div>
        );
      },
      tooltip: (customer) => {
        if (!customer.all_sources) return '';
        const primarySource = customer.all_sources.find(s => s.id === customer.primary_source_id);
        const otherSources = customer.all_sources.filter(s => s.id !== customer.primary_source_id);
        const parts = [];
        if (primarySource) parts.push(`Primary: ${getSourceDisplayName(primarySource)}`);
        if (otherSources.length > 0) parts.push(`Others: ${otherSources.map(s => getSourceDisplayName(s)).join(', ')}`);
        return parts.join(' | ');
      },
    },
    {
      key: 'sources_count',
      label: 'Sources',
      sortable: true,
      width: 135,
      renderCell: (customer) => (
        <div className="text-center">
          <Badge bg="secondary">
            {customer.all_sources?.length || 0}
          </Badge>
        </div>
      ),
      tooltip: (customer) => `${customer.all_sources?.length || 0} source(s)`,
    },
    {
      key: 'conflicts_count',
      label: 'Conflicts',
      sortable: true,
      width: 142,
      renderCell: (customer) => (
        <div className="text-center">
          {customer.conflicts && customer.conflicts.length > 0 ? (
            <Badge bg="warning" text="dark">
              <i className="bi bi-exclamation-triangle me-1"></i>
              {customer.conflicts.length}
            </Badge>
          ) : (
            <Badge bg="success">
              <i className="bi bi-check-circle me-1"></i>
              0
            </Badge>
          )}
        </div>
      ),
      tooltip: (customer) =>
        customer.conflicts && customer.conflicts.length > 0
          ? `${customer.conflicts.length} conflict(s) need resolution`
          : 'No conflicts',
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 180,
      renderCell: (customer) => <small>{formatDateTime(customer.created_at)}</small>,
      tooltip: (customer) => formatDateTime(customer.created_at),
    },
    {
      key: 'updated_at',
      label: 'Updated',
      sortable: true,
      width: 180,
      renderCell: (customer) => <small>{formatDateTime(customer.updated_at)}</small>,
      tooltip: (customer) => formatDateTime(customer.updated_at),
    },
  ];
  }, [integrationSuffixMap]);


  // Handler to toggle missing sling locations filter
  const handleToggleMissingSlingLocationsFilter = () => {
    setFilters(prev => ({
      ...prev,
      missing_sling_locations: prev.missing_sling_locations ? undefined : 'true',
    }));
  };

  // Summary card content
  const stickyHeaderContent = (
    <div className="d-flex align-items-center gap-3 flex-wrap">
      <Card className="">
        <Card.Body className="text-center p-2">
          <div className="mb-2">
            <i className="bi bi-people fs-1 text-primary"></i>
          </div>
          <h3 className="mb-1">{totalCount ?? customers.length}</h3>
          <p className="text-muted mb-0 small">Total Customers</p>
        </Card.Body>
      </Card>

      {/* Missing Sling Locations Filter Card */}
      {summary && summary.missing_sling_locations > 0 && (
        <Card
          className={`summary-card ${filters.missing_sling_locations ? 'summary-card-active' : ''}`}
          onClick={handleToggleMissingSlingLocationsFilter}
          style={{ cursor: 'pointer' }}
        >
          <Card.Body className="text-center p-2">
            <div className="mb-2">
              <i className={`bi bi-exclamation-triangle fs-1 ${filters.missing_sling_locations ? 'text-primary' : 'text-warning'}`}></i>
            </div>
            <h3 className="mb-1">{summary.missing_sling_locations}</h3>
            <p className="text-muted mb-0 small">Customer Locations without Sling Locations</p>
          </Card.Body>
        </Card>
      )}
    </div>
  );

  return (
    <DashboardLayout
      title="Customers"
      subtitle="Unified customer records across all integrations"
      stickyHeader={stickyHeaderContent}
      scrollable
    >
      <style>{`

        /* Clickable summary card styling */
        .summary-card {
          transition: all 0.2s ease;
          border: 2px solid transparent !important;
        }

        .summary-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
        }

        .summary-card-active {
          border: 2px solid #0d6efd !important;
          background-color: rgba(13, 110, 253, 0.05);
        }
      `}</style>

      <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
            <Card.Header className="bg-primary text-white flex-shrink-0">
              <div className="d-flex justify-content-between align-items-center m-1 flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
                    <span className="text-primary fw-semibold">
                      <i className="bi bi-table me-1"></i>
                      Customers
                    </span>
                    <div className="border-end border-primary mx-2" style={{ height: '24px' }}></div>
                    <Button
                      variant={activeFilterCount > 0 ? "primary" : "outline-primary"}
                      size="sm"
                      onClick={() => setShowFilterBar(!showFilterBar)}
                    >
                      <i className="bi bi-funnel me-1"></i>
                      Filters
                      {activeFilterCount > 0 && (
                        <span className="badge bg-light text-primary ms-1">{activeFilterCount}</span>
                      )}
                    </Button>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div className="bg-header-control rounded px-2 py-1">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowCreateModal(true)}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Create Customer
                    </Button>
                  </div>
                  <div className="rounded px-3 py-1">
                    <span className="text-white">
                      {customers.length > 0 ? (
                        <>{totalCount ?? customers.length} total customers</>
                      ) : (
                        'No customers found'
                      )}
                    </span>
                  </div>
                </div>
              </div>
              {showFilterBar && (
                <CustomerFilterBar
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  onClearFilters={handleClearFilters}
                />
              )}
            </Card.Header>
            <Card.Body
              ref={cardBodyRef}
              className="flex-grow-1 overflow-hidden"
              style={{
                padding: 0,
                minHeight: 0,
              }}
            >
              {error ? (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <div className="text-center text-muted">
                    <i className="bi bi-exclamation-triangle fs-1 mb-3 d-block text-danger"></i>
                    <h5>Failed to Load</h5>
                    <p className="mb-0">{(error as any)?.response?.data?.message || (error as any)?.message || 'Failed to load customers'}</p>
                  </div>
                </div>
              ) : (
                <DataTable
                  key={`datatable-${Object.keys(integrationSuffixMap).length}`}
                  data={customers}
                  columns={columns}
                  getRowKey={getRowKey}
                  sortConfig={{ field: sortField, direction: sortDirection }}
                  onSortChange={handleSortChange}
                  onRowClick={handleRowClick}
                  pagination={{
                    hasMore,
                    limit: FETCH_LIMIT,
                  }}
                  onLoadMore={handleLoadMore}
                  isLoading={isLoading}
                  preferencesKey="customers"
                  userId={user?.id}
                  userPreferences={userData?.preferences}
                  maxHeight="100%"
                  enableColumnReorder={true}
                  enableColumnResize={true}
                  enableColumnVisibility={true}
                  enableStickyHeader={true}
                />
              )}
            </Card.Body>
      </Card>

      {/* Create Customer Modal */}
      <Modal show={showCreateModal} onHide={() => validatedAddresses ? handleBackToForm() : setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create Internal Customer</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateCustomer}>
          <Modal.Body>
            {validatedAddresses ? (
              // Show address validation UI
              <>
                <div className="mb-4">
                  <h6 className="mb-3">Your Entered Address:</h6>
                  <Card className="mb-3">
                    <Card.Body>
                      <div>{newCustomerAddress.line1}</div>
                      {newCustomerAddress.line2 && <div>{newCustomerAddress.line2}</div>}
                      <div>
                        {newCustomerAddress.city}, {newCustomerAddress.state} {newCustomerAddress.zip}
                      </div>
                    </Card.Body>
                  </Card>
                </div>

                <div className="mb-4">
                  <h6 className="mb-3">USPS Validated Address:</h6>
                  <Card
                    className="mb-3"
                    style={{
                      borderWidth: '2px',
                      borderColor: validationError ? '#dc3545' : (validatedAddresses.corrections && validatedAddresses.corrections.length > 0 ? '#ffc107' : '#198754'),
                      borderStyle: 'solid',
                      overflow: 'hidden'
                    }}
                  >
                    <Card.Body className="bg-body-tertiary">
                      {validationError ? (
                        <Alert variant="danger" className="mb-0">
                          <i className="bi bi-exclamation-circle me-2"></i>
                          <strong>Validation Error</strong>
                          <p className="mb-0 mt-2">
                            {validationError}
                          </p>
                        </Alert>
                      ) : (
                        <>
                          {validatedAddresses.corrections && validatedAddresses.corrections.length > 0 && (
                            <Alert variant="warning" className="mb-3">
                              <i className="bi bi-exclamation-triangle me-2"></i>
                              <strong>Address Validation</strong>
                              <p className="mb-0 mt-2">
                                {validatedAddresses.corrections[0].userMessage || validatedAddresses.corrections[0].text}
                              </p>
                            </Alert>
                          )}
                          <div>
                            {validatedAddresses.address?.streetAddress || validatedAddresses.address?.streetAddressAbbreviation}
                          </div>
                          {validatedAddresses.address?.secondaryAddress && (
                            <div>{validatedAddresses.address.secondaryAddress}</div>
                          )}
                          <div>
                            {validatedAddresses.address?.city || validatedAddresses.address?.cityAbbreviation}, {validatedAddresses.address?.state}{' '}
                            {validatedAddresses.address?.zipCode}
                            {validatedAddresses.address?.zipPlus4 && `-${validatedAddresses.address.zipPlus4}`}
                          </div>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </div>

                <div className="d-flex gap-2 justify-content-end">
                  <Button variant="outline-secondary" onClick={handleKeepOriginalAddress}>
                    Keep My Address
                  </Button>
                  {!validationError && (
                    <Button
                      variant={validatedAddresses.corrections && validatedAddresses.corrections.length > 0 ? "warning" : "success"}
                      onClick={handleAcceptValidatedAddress}
                    >
                      <i className="bi bi-check-circle me-1"></i>
                      Use USPS Address
                    </Button>
                  )}
                  <Button variant="primary" onClick={handleBackToForm}>
                    <i className="bi bi-pencil me-1"></i>
                    Edit My Address
                  </Button>
                </div>
              </>
            ) : (
              // Show normal form
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Customer Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter customer name"
                    value={newCustomerName}
                    onChange={(e) => setNewCustomerName(e.target.value)}
                    required
                    autoFocus
                    isInvalid={customerNameExists}
                  />
                  {customerNameExists && (
                    <Form.Control.Feedback type="invalid">
                      A customer with this name already exists
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

            <h6 className="mb-3 mt-4">Contact Information</h6>
            <Form.Group className="mb-3">
              <Form.Label>Contact Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter contact name"
                value={newCustomerContact.name}
                onChange={(e) => setNewCustomerContact({ ...newCustomerContact, name: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email address"
                value={newCustomerContact.email}
                onChange={(e) => setNewCustomerContact({ ...newCustomerContact, email: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="tel"
                placeholder="Enter phone number"
                value={newCustomerContact.phone}
                onChange={(e) => setNewCustomerContact({ ...newCustomerContact, phone: e.target.value })}
              />
            </Form.Group>

            <h6 className="mb-3 mt-4">Address</h6>
            <Form.Group className="mb-3">
              <Form.Label>Address Line 1</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter street address"
                value={newCustomerAddress.line1}
                onChange={(e) => setNewCustomerAddress({ ...newCustomerAddress, line1: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Address Line 2</Form.Label>
              <Form.Control
                type="text"
                placeholder="Apartment, suite, etc. (optional)"
                value={newCustomerAddress.line2}
                onChange={(e) => setNewCustomerAddress({ ...newCustomerAddress, line2: e.target.value })}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter city"
                    value={newCustomerAddress.city}
                    onChange={(e) => setNewCustomerAddress({ ...newCustomerAddress, city: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="ST"
                    maxLength={2}
                    value={newCustomerAddress.state}
                    onChange={(e) => setNewCustomerAddress({ ...newCustomerAddress, state: e.target.value.toUpperCase() })}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>ZIP</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="ZIP"
                    value={newCustomerAddress.zip}
                    onChange={(e) => setNewCustomerAddress({ ...newCustomerAddress, zip: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {!validatedAddresses && (
              <>
                <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={!newCustomerName.trim() || customerNameExists || createCustomerMutation.isPending}
                >
                  {createCustomerMutation.isPending ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Creating...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-plus-circle me-1"></i>
                      Create Customer
                    </>
                  )}
                </Button>
              </>
            )}
          </Modal.Footer>
        </Form>
      </Modal>
    </DashboardLayout>
  );
};

export default CustomersPage;
